import { useState } from 'react'
import { Controller } from 'react-hook-form'
import * as yup from 'yup'
import DefinitionList from '@/components/definition-list'
import FormField from '@/components/form-field'
import { Input } from '@/components/input'
import LookupSelect from '@/components/lookup-select'
import Select from '@/components/select'
import { useConselhosQuery, useEspecialidadesLookupQuery, useProfissionaisLookupQuery } from '@/http/queries/lookups'
import type { EspecialidadeSelect, ProfissionalResumo, Credencial } from '@/types/credencial'
import type { LookupItem } from '@/types/lookup'
import { UF_CONSELHO_OPTIONS } from '@/types/domain'
import { useDebouncedValue } from '@/utility/use-debounced-value'
import { orDash } from '@/utility/Utils'
import CredencialForm from '../credencial-form'
import styles from '../style.module.css'

const schema = yup.object({
  nomeSolicitante: yup.string().defined().nullable().default(''),
  conselho: yup.string().defined().nullable().default(''),
  ufConselho: yup.string().defined().nullable().default(''),
  numeroConselho: yup.string().defined().nullable().default(''),
  especialidadeSolicitante: yup
    .mixed<EspecialidadeSelect>()
    .nullable()
    .defined()
    .default(null),
  executantePadrao: yup
    .mixed<ProfissionalResumo>()
    .nullable()
    .defined()
    .default(null),
})

const SolicitanteExecutanteTab = ({ credencial }: { credencial: Credencial }) => {
  const [termoEspecialidade, setTermoEspecialidade] = useState('')
  const termoEspecialidadeDebounced = useDebouncedValue(termoEspecialidade)
  const especialidades = useEspecialidadesLookupQuery(termoEspecialidadeDebounced)

  const [termoExecutante, setTermoExecutante] = useState('')
  const termoExecutanteDebounced = useDebouncedValue(termoExecutante)
  const executantes = useProfissionaisLookupQuery(credencial.clienteId, termoExecutanteDebounced)

  const conselhosQuery = useConselhosQuery()
  const conselhoOptions = (conselhosQuery.data ?? []).map((item) => ({
    value: item.detalhe ?? item.rotulo,
    label: item.detalhe ? `${item.detalhe} — ${item.rotulo}` : item.rotulo,
  }))

  return (
    <CredencialForm
      credencial={credencial}
      schema={schema}
      defaultValues={{
        nomeSolicitante: credencial.nomeSolicitante ?? '',
        conselho: credencial.conselho ?? '',
        ufConselho: credencial.ufConselho ?? '',
        numeroConselho: credencial.numeroConselho ?? '',
        especialidadeSolicitante: credencial.especialidadeSolicitante,
        executantePadrao: credencial.executantePadrao,
      }}
      montarPayload={(dados) => ({
        ...credencial,
        nomeSolicitante: dados.nomeSolicitante || null,
        conselho: dados.conselho || null,
        ufConselho: dados.ufConselho || null,
        numeroConselho: dados.numeroConselho || null,
        especialidadeSolicitante: dados.especialidadeSolicitante ?? null,
        executantePadrao: dados.executantePadrao ?? null,
      })}
    >
      {({ control, register }, podeEditar) => (
        <>
          <div className={styles.grid}>
            <FormField label="Nome do solicitante" htmlFor="nomeSolicitante">
              <Input id="nomeSolicitante" disabled={!podeEditar} {...register('nomeSolicitante')} />
            </FormField>

            <FormField label="Especialidade do solicitante" htmlFor="especialidadeSolicitante">
              <Controller
                control={control}
                name="especialidadeSolicitante"
                render={({ field }) => {
                  const opcoes: LookupItem[] = (especialidades.data ?? []).map((item) => ({
                    id: item.id,
                    rotulo: item.rotulo,
                    detalhe: null,
                  }))
                  const selecionado: LookupItem | null = field.value
                    ? { id: String(field.value.value), rotulo: field.value.label ?? '', detalhe: null }
                    : null

                  return podeEditar ? (
                    <LookupSelect
                      id="especialidadeSolicitante"
                      termo={termoEspecialidade}
                      onTermoChange={setTermoEspecialidade}
                      itens={opcoes}
                      isLoading={especialidades.isFetching}
                      mostrarOpcoesSemBusca
                      selecionado={selecionado}
                      placeholder="Preencha o nome da especialidade"
                      onSelecionar={(item) => {
                        if (!item) {
                          field.onChange(null)
                          return
                        }
                        const opcao = (especialidades.data ?? []).find(
                          (candidato) => candidato.id === item.id,
                        )
                        if (opcao) field.onChange({ label: opcao.rotulo, value: Number(opcao.id) })
                      }}
                    />
                  ) : (
                    <Input
                      id="especialidadeSolicitante"
                      value={field.value?.label ?? ''}
                      disabled
                      readOnly
                    />
                  )
                }}
              />
            </FormField>

            <FormField label="Conselho" htmlFor="conselho">
              <Controller
                control={control}
                name="conselho"
                render={({ field }) => (
                  <Select
                    id="conselho"
                    options={conselhoOptions}
                    isLoading={conselhosQuery.isFetching}
                    disabled={!podeEditar}
                    {...field}
                    value={field.value ?? ''}
                  />
                )}
              />
            </FormField>

            <FormField label="UF do conselho" htmlFor="ufConselho">
              <Select
                id="ufConselho"
                options={UF_CONSELHO_OPTIONS}
                disabled={!podeEditar}
                {...register('ufConselho')}
              />
            </FormField>

            <FormField label="Número do conselho" htmlFor="numeroConselho">
              <Input id="numeroConselho" disabled={!podeEditar} {...register('numeroConselho')} />
            </FormField>

            <FormField label="Executante padrão" htmlFor="executantePadrao">
              <Controller
                control={control}
                name="executantePadrao"
                render={({ field }) => {
                  const opcoes: LookupItem[] = (executantes.data ?? []).map((item) => ({
                    id: item.id,
                    rotulo: item.rotulo,
                    detalhe: item.detalhe,
                  }))
                  const selecionado: LookupItem | null = field.value
                    ? { id: field.value.id ?? '', rotulo: field.value.nome ?? '', detalhe: null }
                    : null

                  return podeEditar ? (
                    <LookupSelect
                      id="executantePadrao"
                      termo={termoExecutante}
                      onTermoChange={setTermoExecutante}
                      itens={opcoes}
                      isLoading={executantes.isFetching}
                      mostrarOpcoesSemBusca
                      selecionado={selecionado}
                      placeholder="Preencha o nome do executante"
                      onSelecionar={(item) => {
                        if (!item) {
                          field.onChange(null)
                          return
                        }
                        field.onChange({
                          id: item.id,
                          nome: item.rotulo,
                          cpfCnpj: null,
                          registroConselho: item.detalhe,
                          ufConselho: null,
                        })
                      }}
                    />
                  ) : (
                    <Input
                      id="executantePadrao"
                      value={field.value?.nome ?? ''}
                      disabled
                      readOnly
                    />
                  )
                }}
              />
            </FormField>
          </div>

          {credencial.executantePadrao &&
          (credencial.executantePadrao.cpfCnpj || credencial.executantePadrao.registroConselho) ? (
            <DefinitionList
              title="Dados do executante reconhecidos pelo sistema"
              items={[
                { label: 'CPF/CNPJ', value: orDash(credencial.executantePadrao.cpfCnpj) },
                { label: 'Conselho', value: orDash(credencial.executantePadrao.registroConselho) },
                { label: 'UF', value: orDash(credencial.executantePadrao.ufConselho) },
              ]}
            />
          ) : null}
        </>
      )}
    </CredencialForm>
  )
}

export default SolicitanteExecutanteTab
